import React, { useEffect, useState } from 'react';

import type {
  ByfunnFrapiArticle,
  ByfunnPromoArticle,
} from '../../../../types/frapi-types.js';
import type { SaveListItem } from '../../../../types/save-types.js';
import type { Tag } from '../../../../../core/data-sources/frapi/data-types.js';

import { useLocalStorage } from '../../../hooks/useLocalStorage.js';
import BigGrade from './BigGrade.js';
import Tags from './Tags.js';
import { usePulseContext } from '../../../../../core/mandatory-integrations/pulse/PulseProvider.js';
import { itemIsSaved } from '../../saving/utils.js';
import SaveButton from './SaveButton.js';
import { useListsContext } from '../../saving/ListsProvider.js';
import SaveArticleReminder from './SaveArticleReminder.js';
import Price from './Price.js';
import { formatRestaurantUrl } from '../helpers.js';

export type AboutProps = {
  article: ByfunnFrapiArticle | ByfunnPromoArticle;
  handleOverlay: (item: SaveListItem) => void;
  item: SaveListItem;
  isRestaurantReview?: boolean;
};

export const getLocationTag = (tags: Tag[]) => {
  const tagsArray = tags.filter((tag) => tag.title.startsWith('Vink/område'));
  const tagTitle = tagsArray[0]?.title.split('/')[2];
  const tagSlug = '/tag/' + tagsArray[0]?.slug;

  return tagsArray.length > 0 ? <a href={tagSlug}>, {tagTitle}</a> : '';
};

const About: React.FC<AboutProps> = ({
  article,
  handleOverlay,
  item,
  isRestaurantReview = true,
}) => {
  const { tags } = article;
  const { trackElementClick } = usePulseContext();
  const { savedItems, isLoading } = useListsContext();
  const [isSaved, setIsSaved] = useState(false);
  const [showSaveReminder, setShowSaveReminder] = useLocalStorage(
    'showSaveReminder',
    true,
  );

  useEffect(() => {
    if (savedItems.length > 0) {
      setIsSaved(itemIsSaved(item, savedItems));
    }
  }, [item, savedItems]);

  const handleHideReminder = () => {
    trackElementClick({
      object: {
        id: `article:hide-save-reminder`,
        name: 'Hide save reminder',
      },
    });
    setShowSaveReminder(false);
  };

  const handleOnClick = () => {
    trackElementClick({
      object: {
        id: 'article:save-button',
        name: `Save ${isRestaurantReview ? 'restaurant' : 'movie'} article`,
      },
    }).finally(() => {
      handleOverlay(item);
      if (showSaveReminder) {
        setShowSaveReminder(false);
      }
    });
  };

  const shouldRenderSaveReminder = showSaveReminder && !isSaved && !isLoading;

  const Description = () => (
    <>
      <p className="description-title">
        {isRestaurantReview ? 'Her får du' : 'Se den fordi'}
      </p>
      <p className="description">{article.description}</p>
    </>
  );

  const Buttons = ({ children }: { children?: React.ReactNode }) => (
    <div className="buttons">
      {children}
      {!isLoading && (
        <SaveButton
          handleOnClick={handleOnClick}
          item={item}
          isSaved={isSaved}
          isFact
        />
      )}
      {shouldRenderSaveReminder && (
        <SaveArticleReminder handleClose={handleHideReminder} />
      )}
    </div>
  );

  const RestaurantInfo = () => (
    <div className="about">
      <h2 className="title">{article?.restaurantTitle}</h2>
      <p className="address">
        {article?.restaurantAddress}
        {getLocationTag(tags)}
      </p>
      <div className="review-tags">
        <div className="tag-container">
          <Tags tags={tags} type={'mattype'} noBorder />
        </div>
        {article.priceLevel && article.priceLevel !== 'none' && (
          <Price price={article.priceLevel} />
        )}
      </div>
      {article.grade && <BigGrade grade={article.grade} isRestaurantReview />}
      <Description />
      <Buttons>
        <span className="url-buttons">
          {article.restaurant_url && (
            <a
              className="button homepage"
              href={formatRestaurantUrl(article.restaurant_url)}
              target="_blank"
              rel="noreferrer"
            >
              Nettside
            </a>
          )}
          {article.googleMapsLink && (
            <a
              className="button map"
              href={article.googleMapsLink}
              target="_blank"
              rel="noreferrer"
            >
              Kart
            </a>
          )}
        </span>
      </Buttons>
    </div>
  );

  const MovieAbout = () => (
    <div className="about movie">
      <h2 className="title">{article?.restaurantTitle}</h2>
      <Tags tags={tags} type={['format', 'type']} noBorder regular />
      <Tags tags={tags} type="strømmetjeneste" noBorder />
      {article.grade && (
        <BigGrade grade={article.grade} isRestaurantReview={false} />
      )}

      <Description />

      <Buttons />
    </div>
  );

  return isRestaurantReview ? <RestaurantInfo /> : <MovieAbout />;
};

export default About;
